import { ActionButton, NumberField, Text, TextField } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import Close from '@react-spectrum/s2/icons/Close';
import {
	useCallback,
	useRef,
	useState,
	type PointerEvent as ReactPointerEvent,
} from 'react';

import { AnimateIn } from '@/components/AnimateIn';
import { EditableTagList } from '@/components/EditableTagList';
import { useEditableTagList } from '@/hooks/useEditableTagList';
import { useOptimisticState } from '@/hooks/useOptimisticState';

import type {
	BusinessProfile,
	PositioningItem,
} from '../../../../../bridge/types/business-profile.ts';
import { EditableCard } from '../EditableCard';
import { PILL_TAG } from '../styles';

type BusinessSummaryTabProps = {
	profile: BusinessProfile | undefined;
	onSave: (path: string, value: string) => void;
	onArraySave: (path: string, value: string[]) => void;
	onNumberSave: (path: string, value: number) => void;
	onIcpAgeSave: (index: number, value: { start: number; end: number }) => void;
	onPositioningSave: (value: PositioningItem[]) => void;
};

type IcpDraft = {
	name: string;
	occupation: string[];
	income: number | null;
	ageStart: number;
	ageEnd: number;
};

// A draft axis carries a client-only stable key so React can keep input focus
// while the (still-blank) left/right labels change during editing.
type PosDraftRow = PositioningItem & { _uid: number };

const AGE_MIN = 18;
const AGE_MAX = 80;

const SECTION_LABEL = 'text-sm font-normal text-[var(--text-secondary)]';

export function BusinessSummaryTab({
	profile,
	onSave,
	onArraySave,
	onNumberSave,
	onIcpAgeSave,
	onPositioningSave,
}: BusinessSummaryTabProps) {
	const [nameEditing, setNameEditing] = useState(false);
	const nameDraftRef = useRef('');
	const businessName = profile?.my_business?.business_name;
	const [nameDisplay, setOptimisticName] = useOptimisticState(
		businessName?.value ?? '',
	);

	const [goalEditing, setGoalEditing] = useState(false);
	const goalDraftRef = useRef('');
	const goal = profile?.my_business?.marketing_goal;
	const [goalDisplay, setOptimisticGoal] = useOptimisticState(
		goal?.value ?? '',
	);

	const [budgetEditing, setBudgetEditing] = useState(false);
	const budgetDraftRef = useRef<number | null>(null);
	const budget = profile?.my_business?.marketing_budget;
	const [budgetDisplay, setOptimisticBudget] = useOptimisticState<
		number | null
	>(budget?.value ?? null);

	const differentiator = profile?.my_business?.differentiator;
	const diffTags = useEditableTagList({
		serverValue: differentiator?.value ?? [],
		onSave: (values) => onArraySave('my_business.differentiator', values),
	});

	const serviceAreaValues = profile?.my_business?.service_area?.value;
	const serviceArea =
		serviceAreaValues && serviceAreaValues.length > 0
			? serviceAreaValues
			: null;
	const svcTags = useEditableTagList({
		serverValue: serviceArea ?? [],
		onSave: (values) => onArraySave('my_business.service_area', values),
	});

	const icp = profile?.ideal_customer_profiles?.[0];
	const occupationTags = icp?.occupation?.value ?? [];
	const icpName = icp?.name?.value ?? icp?.archetype?.value ?? '';
	const ageStart = icp?.age?.value?.start ?? 25;
	const ageEnd = icp?.age?.value?.end ?? 75;
	const incomeDisplay =
		icp?.income?.value != null ? `$${icp.income.value.toLocaleString()}` : '';

	const [icpEditing, setIcpEditing] = useState(false);
	const [optimisticIcp, setOptimisticIcp] = useOptimisticState<{
		name: string;
		occupation: string[];
		income: number | null;
		ageStart: number;
		ageEnd: number;
	} | null>(null);
	const icpDraftRef = useRef<IcpDraft>({
		name: '',
		occupation: [],
		income: null,
		ageStart: 25,
		ageEnd: 75,
	});
	const [icpDraftOccupation, setIcpDraftOccupation] = useState<string[]>([]);
	const [newOccValue, setNewOccValue] = useState('');
	const [icpDraftAgeStart, setIcpDraftAgeStart] = useState(25);
	const [icpDraftAgeEnd, setIcpDraftAgeEnd] = useState(75);
	const [icpDraftIncome, setIcpDraftIncome] = useState<number | undefined>(
		undefined,
	);

	const displayIcpName = optimisticIcp?.name ?? icpName;
	const displayOccupation = optimisticIcp?.occupation ?? occupationTags;
	const displayAgeStart = optimisticIcp?.ageStart ?? ageStart;
	const displayAgeEnd = optimisticIcp?.ageEnd ?? ageEnd;
	const displayIncome =
		optimisticIcp !== null
			? optimisticIcp.income != null
				? `$${optimisticIcp.income.toLocaleString()}`
				: ''
			: incomeDisplay;

	const [posEditing, setPosEditing] = useState(false);
	const [posDraft, setPosDraft] = useState<PosDraftRow[]>([]);
	const posUidRef = useRef(0);
	const newPosRow = (init?: Partial<PositioningItem>): PosDraftRow => ({
		left: '',
		right: '',
		score: 5,
		reasoning: '',
		...init,
		_uid: posUidRef.current++,
	});
	// One immutable by-uid patch shared by every field editor, so the
	// left/right/score handlers don't each re-implement the map-by-uid closure.
	const updatePosRow = (uid: number, patch: Partial<PositioningItem>) =>
		setPosDraft((d) => d.map((r) => (r._uid === uid ? { ...r, ...patch } : r)));

	const serverPos = profile?.brand_identity?.positioning?.value;
	const [positioningItems, setOptimisticPos] = useOptimisticState(
		serverPos ?? [],
	);

	return (
		<div className="flex flex-col gap-[22px]">
			<div className="grid grid-cols-2 gap-[22px]">
				<div className="flex flex-col gap-[22px]">
					<AnimateIn delay={0}>
						<EditableCard
							title="Business Name"
							isEditing={nameEditing}
							onEdit={() => {
								nameDraftRef.current = businessName?.value ?? '';
								setNameEditing(true);
							}}
							onSave={() => {
								setOptimisticName(nameDraftRef.current);
								onSave('my_business.business_name', nameDraftRef.current);
								setNameEditing(false);
							}}
							onCancel={() => setNameEditing(false)}
						>
							{nameEditing ? (
								<TextField
									aria-label="Business name"
									defaultValue={businessName?.value ?? ''}
									onChange={(val) => {
										nameDraftRef.current = val;
									}}
								/>
							) : (
								<p
									className="flex-1 text-sm leading-relaxed
										text-[var(--text-primary)]"
								>
									{nameDisplay || (
										<span className="text-[var(--text-muted)]">
											What is your business name?
										</span>
									)}
								</p>
							)}
						</EditableCard>
					</AnimateIn>

					<AnimateIn delay={1}>
						<EditableCard
							title="Monthly marketing budget"
							isEditing={budgetEditing}
							onEdit={() => {
								budgetDraftRef.current = budgetDisplay;
								setBudgetEditing(true);
							}}
							onSave={() => {
								const value = budgetDraftRef.current;
								if (value != null && Number.isFinite(value)) {
									setOptimisticBudget(value);
									onNumberSave('my_business.marketing_budget', value);
								}
								setBudgetEditing(false);
							}}
							onCancel={() => setBudgetEditing(false)}
						>
							{budgetEditing ? (
								<NumberField
									aria-label="Monthly marketing budget"
									defaultValue={budgetDisplay ?? undefined}
									minValue={0}
									formatOptions={{
										style: 'currency',
										currency: 'USD',
										maximumFractionDigits: 0,
									}}
									onChange={(val) => {
										budgetDraftRef.current = val;
									}}
								/>
							) : (
								<div className="flex flex-wrap gap-1.5">
									{budgetDisplay != null ? (
										<span className={PILL_TAG}>
											{`$${budgetDisplay.toLocaleString()}/month`}
										</span>
									) : (
										<span className="text-sm text-[var(--text-muted)]">
											No budget set yet
										</span>
									)}
								</div>
							)}
						</EditableCard>
					</AnimateIn>
				</div>

				<AnimateIn delay={0} className="h-full">
					<EditableCard
						title="Goal"
						isEditing={goalEditing}
						onEdit={() => {
							goalDraftRef.current = goal?.value ?? '';
							setGoalEditing(true);
						}}
						onSave={() => {
							setOptimisticGoal(goalDraftRef.current);
							onSave('my_business.marketing_goal', goalDraftRef.current);
							setGoalEditing(false);
						}}
						onCancel={() => setGoalEditing(false)}
						className="h-full"
					>
						{goalEditing ? (
							<textarea
								defaultValue={goal?.value ?? ''}
								onChange={(e) => {
									goalDraftRef.current = e.target.value;
								}}
								className="w-full resize-none rounded-lg border-2
									border-[#131313] bg-white px-3 py-2 text-sm"
								rows={4}
							/>
						) : (
							<p
								className="flex-1 text-sm leading-relaxed
									text-[var(--text-primary)]"
							>
								{goalDisplay || (
									<span className="text-[var(--text-muted)]">
										What is your business goal?
									</span>
								)}
							</p>
						)}
					</EditableCard>
				</AnimateIn>
			</div>

			<div className="grid grid-cols-2 gap-[22px]">
				<AnimateIn delay={1} className="h-full">
					<EditableCard
						title="Differentiators"
						isEditing={diffTags.isEditing}
						onEdit={diffTags.onEdit}
						onSave={diffTags.onSave}
						onCancel={diffTags.onCancel}
						className="h-full"
					>
						{diffTags.isEditing ? (
							<EditableTagList
								tags={diffTags}
								inputComponent="TextArea"
								placeholder="Define differentiator"
							/>
						) : (
							<div>
								{diffTags.displayTags.length > 0 ? (
									<ul
										className="list-disc space-y-2.5 pl-5 text-sm
											text-[var(--text-primary)]"
									>
										{diffTags.displayTags.map((tag) => (
											<li key={tag}>{tag}</li>
										))}
									</ul>
								) : (
									<span className="text-sm text-[var(--text-muted)]">
										No differentiators yet
									</span>
								)}
							</div>
						)}
					</EditableCard>
				</AnimateIn>

				<AnimateIn delay={2} className="h-full">
					<EditableCard
						title="Service Area"
						isEditing={svcTags.isEditing}
						onEdit={svcTags.onEdit}
						onSave={svcTags.onSave}
						onCancel={svcTags.onCancel}
						className="h-full"
					>
						{svcTags.isEditing ? (
							<EditableTagList tags={svcTags} placeholder="Add service area" />
						) : (
							<div className="flex flex-col gap-2">
								{svcTags.displayTags.length > 0 ? (
									svcTags.displayTags.map((area, i) => (
										<div key={area} className="flex items-center gap-3 text-sm">
											<span
												className="bg-beacon-blue flex h-6 w-6 shrink-0
													items-center justify-center rounded-full text-xs
													font-semibold text-white"
											>
												{i + 1}
											</span>
											<span className="text-[var(--text-primary)]">{area}</span>
										</div>
									))
								) : (
									<span className="text-sm text-[var(--text-muted)]">
										No service areas yet
									</span>
								)}
							</div>
						)}
					</EditableCard>
				</AnimateIn>
			</div>

			<div className="grid grid-cols-2 gap-[22px]">
				<AnimateIn delay={3} className="h-full">
					<EditableCard
						title="Ideal Customer"
						isEditing={icpEditing}
						onEdit={() => {
							const draft: IcpDraft = {
								name: optimisticIcp?.name ?? icpName,
								occupation: [...(optimisticIcp?.occupation ?? occupationTags)],
								income:
									optimisticIcp !== null
										? optimisticIcp.income
										: (icp?.income?.value ?? null),
								ageStart: optimisticIcp?.ageStart ?? ageStart,
								ageEnd: optimisticIcp?.ageEnd ?? ageEnd,
							};
							icpDraftRef.current = draft;
							setIcpDraftOccupation([...draft.occupation]);
							setNewOccValue('');
							setIcpDraftAgeStart(draft.ageStart);
							setIcpDraftAgeEnd(draft.ageEnd);
							setIcpDraftIncome(draft.income ?? undefined);
							setIcpEditing(true);
						}}
						onSave={() => {
							if (newOccValue.trim()) {
								const val = newOccValue.trim();
								if (!icpDraftRef.current.occupation.includes(val)) {
									icpDraftRef.current.occupation = [
										...icpDraftRef.current.occupation,
										val,
									];
								}
							}
							const d = icpDraftRef.current;
							setOptimisticIcp({
								name: d.name,
								occupation: d.occupation,
								income: d.income,
								ageStart: d.ageStart,
								ageEnd: d.ageEnd,
							});
							onSave('ideal_customer_profiles.0.name', d.name);
							onArraySave('ideal_customer_profiles.0.occupation', d.occupation);
							if (d.income != null) {
								onNumberSave('ideal_customer_profiles.0.income', d.income);
							}
							onIcpAgeSave(0, { start: d.ageStart, end: d.ageEnd });
							setIcpEditing(false);
						}}
						onCancel={() => setIcpEditing(false)}
						className="h-full"
					>
						{icpEditing ? (
							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-1.5">
									<span className={SECTION_LABEL}>Name</span>
									<TextField
										aria-label="Customer name"
										defaultValue={icpDraftRef.current.name}
										onChange={(val) => {
											icpDraftRef.current.name = val;
										}}
									/>
								</div>

								<div className="flex flex-col gap-1.5">
									<span className={SECTION_LABEL}>Occupation</span>
									<EditableTagList
										tags={{
											draftTags: icpDraftOccupation,
											newValue: newOccValue,
											isEditing: true,
											displayTags: displayOccupation,
											onEdit: () => {},
											onSave: () => {},
											onCancel: () => {},
											onUpdateTag: (i, val) => {
												icpDraftRef.current.occupation[i] = val;
												setIcpDraftOccupation([
													...icpDraftRef.current.occupation,
												]);
											},
											onRemoveTag: (i) => {
												icpDraftRef.current.occupation =
													icpDraftRef.current.occupation.filter(
														(_, idx) => idx !== i,
													);
												setIcpDraftOccupation([
													...icpDraftRef.current.occupation,
												]);
											},
											onAddTag: () => {
												if (newOccValue.trim()) {
													const val = newOccValue.trim();
													if (!icpDraftRef.current.occupation.includes(val)) {
														icpDraftRef.current.occupation = [
															...icpDraftRef.current.occupation,
															val,
														];
														setIcpDraftOccupation([
															...icpDraftRef.current.occupation,
														]);
													}
													setNewOccValue('');
												}
											},
											setNewValue: setNewOccValue,
										}}
										placeholder="Add occupation"
									/>
								</div>

								<div className="flex flex-col gap-1.5">
									<span className={SECTION_LABEL}>Income</span>
									<NumberField
										aria-label="Income"
										value={icpDraftIncome}
										formatOptions={{
											style: 'currency',
											currency: 'USD',
											maximumFractionDigits: 0,
										}}
										onChange={(val) => {
											icpDraftRef.current.income = val;
											setIcpDraftIncome(val);
										}}
									/>
								</div>

								<div className="flex flex-col gap-1.5">
									<div className="flex items-center justify-between">
										<span className={SECTION_LABEL}>Age</span>
										<span className="text-xs text-[#505050]">
											{icpDraftAgeStart} - {icpDraftAgeEnd}
										</span>
									</div>
									<AgeRangeSlider
										start={icpDraftAgeStart}
										end={icpDraftAgeEnd}
										onChange={(s, e) => {
											icpDraftRef.current.ageStart = s;
											icpDraftRef.current.ageEnd = e;
											setIcpDraftAgeStart(s);
											setIcpDraftAgeEnd(e);
										}}
									/>
								</div>
							</div>
						) : (
							<>
								<div className="flex items-center gap-3">
									<div
										className="flex h-12 w-12 shrink-0 items-center
											justify-center rounded-full bg-[var(--glass-border)]
											text-sm font-semibold text-[var(--text-secondary)]"
									>
										{displayIcpName.charAt(0).toUpperCase()}
									</div>
									<div className="flex flex-col">
										<span
											className="text-sm font-semibold
												text-[var(--text-primary)]"
										>
											{displayIcpName}
										</span>
									</div>
								</div>

								<div className="mt-4 flex flex-col gap-1.5">
									<span className={SECTION_LABEL}>Occupation</span>
									<div className="flex flex-wrap gap-1.5">
										{displayOccupation.map((tag) => (
											<span key={tag} className={PILL_TAG}>
												{tag}
											</span>
										))}
									</div>
								</div>

								<div className="mt-3 flex flex-col gap-1.5">
									<span className={SECTION_LABEL}>Income</span>
									<div className="flex flex-wrap gap-1.5">
										<span className={PILL_TAG}>{displayIncome}</span>
									</div>
								</div>

								<div className="mt-3 flex flex-col gap-1.5">
									<div className="flex items-center justify-between">
										<span className={SECTION_LABEL}>Age</span>
										<span className="text-xs text-[#505050]">
											{displayAgeStart} - {displayAgeEnd}
										</span>
									</div>
									<div className="relative h-1 rounded bg-[#e9e9e9]">
										<div
											className="absolute h-full rounded bg-[#131313]
												opacity-30"
											style={{
												left: `${((displayAgeStart - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
												right: `${100 - ((displayAgeEnd - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
											}}
										/>
										<div
											className="absolute top-1/2 size-5 -translate-x-1/2
												-translate-y-1/2 rounded-full border-2 border-[#131313]
												bg-white"
											style={{
												left: `${((displayAgeStart - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
											}}
										/>
										<div
											className="absolute top-1/2 size-5 -translate-x-1/2
												-translate-y-1/2 rounded-full border-2 border-[#131313]
												bg-white"
											style={{
												left: `${((displayAgeEnd - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
											}}
										/>
									</div>
								</div>
							</>
						)}
					</EditableCard>
				</AnimateIn>

				<AnimateIn delay={3} className="h-full">
					<EditableCard
						title="Positioning"
						isEditing={posEditing}
						onEdit={() => {
							setPosDraft(positioningItems.map((p) => newPosRow(p)));
							setPosEditing(true);
						}}
						onSave={() => {
							// Drop any blank rows, then persist — same "empty row is
							// ignored" contract as the tag lists.
							const saved: PositioningItem[] = posDraft
								.filter((r) => r.left.trim() && r.right.trim())
								.map(({ left, right, score, reasoning }) => ({
									left: left.trim(),
									right: right.trim(),
									score,
									reasoning,
								}));
							setOptimisticPos(saved);
							onPositioningSave(saved);
							setPosEditing(false);
						}}
						onCancel={() => setPosEditing(false)}
						className="h-full"
					>
						{posEditing ? (
							<PositioningEditor
								rows={posDraft}
								onUpdate={updatePosRow}
								onRemove={(uid) =>
									setPosDraft((d) => d.filter((r) => r._uid !== uid))
								}
								onAdd={() => setPosDraft((d) => [...d, newPosRow()])}
							/>
						) : (
							<div className="flex flex-col gap-8">
								{positioningItems.length > 0 ? (
									positioningItems.map((item, i) => (
										<div key={i} className="flex flex-col gap-3">
											<div
												className="flex justify-between text-sm text-[#505050]"
											>
												<span>{item.left}</span>
												<span>{item.right}</span>
											</div>
											<div className="relative h-1 rounded bg-[#e9e9e9]">
												<div
													className="absolute top-1/2 size-5 -translate-x-1/2
														-translate-y-1/2 rounded-full border-2
														border-[#131313] bg-white"
													style={{
														left: `${item.score * 10}%`,
													}}
												/>
											</div>
										</div>
									))
								) : (
									<span className="text-sm text-[var(--text-muted)]">
										No positioning axes yet
									</span>
								)}
							</div>
						)}
					</EditableCard>
				</AnimateIn>
			</div>
		</div>
	);
}

const AgeRangeSlider = ({
	start,
	end,
	onChange,
}: {
	start: number;
	end: number;
	onChange: (start: number, end: number) => void;
}) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<'start' | 'end' | null>(null);

	const pct = (val: number) => ((val - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100;

	const valueFromX = useCallback((clientX: number) => {
		const track = trackRef.current;
		if (!track) return AGE_MIN;
		const rect = track.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		return Math.round(AGE_MIN + ratio * (AGE_MAX - AGE_MIN));
	}, []);

	const handlePointerDown = useCallback(
		(thumb: 'start' | 'end') => (e: ReactPointerEvent) => {
			e.preventDefault();
			setDragging(thumb);

			const onMove = (me: globalThis.PointerEvent) => {
				const val = valueFromX(me.clientX);
				if (thumb === 'start') {
					onChange(Math.min(val, end), end);
				} else {
					onChange(start, Math.max(val, start));
				}
			};

			const onUp = () => {
				setDragging(null);
				document.removeEventListener('pointermove', onMove);
				document.removeEventListener('pointerup', onUp);
			};

			document.addEventListener('pointermove', onMove);
			document.addEventListener('pointerup', onUp);
		},
		[valueFromX, onChange, start, end],
	);

	return (
		<div ref={trackRef} className="relative h-1 rounded bg-[#e9e9e9]">
			<div
				className="absolute h-full rounded bg-[#131313] opacity-30"
				style={{
					left: `${pct(start)}%`,
					right: `${100 - pct(end)}%`,
				}}
			/>
			<div
				data-testid="age-thumb-start"
				className={`absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2
					rounded-full border-2 border-[#131313] bg-white
					${dragging === 'start' ? 'cursor-grabbing' : 'cursor-grab'}`}
				style={{ left: `${pct(start)}%`, touchAction: 'none' }}
				onPointerDown={handlePointerDown('start')}
			/>
			<div
				data-testid="age-thumb-end"
				className={`absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2
					rounded-full border-2 border-[#131313] bg-white
					${dragging === 'end' ? 'cursor-grabbing' : 'cursor-grab'}`}
				style={{ left: `${pct(end)}%`, touchAction: 'none' }}
				onPointerDown={handlePointerDown('end')}
			/>
		</div>
	);
};

const PositioningEditor = ({
	rows,
	onUpdate,
	onRemove,
	onAdd,
}: {
	rows: PosDraftRow[];
	onUpdate: (uid: number, patch: Partial<PositioningItem>) => void;
	onRemove: (uid: number) => void;
	onAdd: () => void;
}) => (
	<div className="flex flex-col gap-8">
		{rows.map((row, i) => (
			<div key={row._uid} className="flex flex-col gap-3">
				<div className="flex items-start gap-2">
					<div className="flex flex-1 justify-between gap-2">
						<TextField
							aria-label={`Positioning left label ${i + 1}`}
							value={row.left}
							placeholder="Left label"
							onChange={(val) => onUpdate(row._uid, { left: val })}
						/>
						<TextField
							aria-label={`Positioning right label ${i + 1}`}
							value={row.right}
							placeholder="Right label"
							onChange={(val) => onUpdate(row._uid, { right: val })}
						/>
					</div>
					<ActionButton
						aria-label={`Remove axis ${i + 1}`}
						isQuiet
						size="XS"
						onPress={() => onRemove(row._uid)}
					>
						<Close />
					</ActionButton>
				</div>
				<PositioningSlider
					index={i}
					value={row.score}
					onChange={(val) => onUpdate(row._uid, { score: val })}
				/>
			</div>
		))}
		<ActionButton
			aria-label="Add axis"
			isQuiet
			size="S"
			onPress={onAdd}
			UNSAFE_className="self-start"
		>
			<Add />
			<Text>Add axis</Text>
		</ActionButton>
	</div>
);

const PositioningSlider = ({
	value,
	onChange,
	index,
}: {
	value: number;
	onChange: (value: number) => void;
	index?: number;
}) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState(false);

	const valueFromX = useCallback((clientX: number) => {
		const track = trackRef.current;
		if (!track) return 0;
		const rect = track.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		return Math.round(ratio * 10);
	}, []);

	const handlePointerDown = useCallback(
		(e: ReactPointerEvent) => {
			e.preventDefault();
			setDragging(true);

			const onMove = (me: globalThis.PointerEvent) => {
				onChange(valueFromX(me.clientX));
			};

			const onUp = () => {
				setDragging(false);
				document.removeEventListener('pointermove', onMove);
				document.removeEventListener('pointerup', onUp);
			};

			document.addEventListener('pointermove', onMove);
			document.addEventListener('pointerup', onUp);
		},
		[valueFromX, onChange],
	);

	const handleTrackClick = useCallback(
		(e: ReactPointerEvent) => {
			onChange(valueFromX(e.clientX));
		},
		[valueFromX, onChange],
	);

	return (
		<div
			ref={trackRef}
			className="relative h-1 cursor-pointer rounded bg-[#e9e9e9]"
			onPointerDown={handleTrackClick}
		>
			<div
				data-testid={
					index !== undefined ? `positioning-thumb-${index}` : undefined
				}
				className={`absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2
					rounded-full border-2 border-[#131313] bg-white
					${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
				style={{ left: `${value * 10}%`, touchAction: 'none' }}
				onPointerDown={(e) => {
					e.stopPropagation();
					handlePointerDown(e);
				}}
			/>
		</div>
	);
};
